<template>
    <div class="dropdown" :class="themeClass" @click.stop="showOptions = true">
        <div class="dropdown__text">
            <slot :value="value" :text="selectedText" />
        </div>

        <ul
            v-if="showOptions"
            v-on-clickaway="onOptionsClose"
            class="dropdown__list"
        >
            <li
                v-for="(option, index) in filteredOptions"
                :key="index"
                class="dropdown__item"
                @click.stop="onOptionSelect(option)"
            >
                <slot :text="option.text" :value="option.value" />
            </li>
        </ul>
    </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import listenOnRootMixin from '@/mixins/listen-on-root';

export default {
    mixins: [clickaway, listenOnRootMixin],

    props: {
        options: {
            type: Array,
            default: () => [
                { text: 'option 1', value: 1 },
                { text: 'option 2', value: 2 },
                { text: 'option 3', value: 3 },
            ],
        },

        placeholder: {
            type: String,
            default: 'Select Option',
        },

        theme: {
            type: String,
            default: '', // currently only one theme - "minimalistic"
        },

        value: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            showOptions: false,
            selectedText: this.placeholder,
        };
    },

    computed: {
        filteredOptions() {
            return this.options.filter(option => option.value !== this.value);
        },

        themeClass() {
            switch (this.theme) {
                case 'minimalistic':
                    return 'dropdown--minimalistic';

                case 'toolbar':
                    return 'dropdown--toolbar';

                default:
                    return '';
            }
        },
    },

    watch: {
        value: {
            handler(newVal) {
                const newOption = this.options.find(option => option.value === newVal);

                this.selectedText = newOption ? newOption.text : this.placeholder;
            },
            immediate: true,
        },
    },

    mounted() {
        // needed as v-clickaway doesn't trigger in certain situations
        this.listenOnRoot('flex::dropdown::close', this.onOptionsClose);
    },

    methods: {
        onOptionSelect(val) {
            this.onOptionsClose();
            this.selectedText = val.text;
            this.$emit('input', val.value);
        },

        onOptionsClose() {
            this.showOptions = false;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_includes.scss';

.dropdown {
    $this: &;
    position: relative;
    color: var(--form-inputColor);
    background-color: var(--form-inputBackgroundColor);
    border: 1px solid var(--form-inputBorderColor);

    &--minimalistic {
        border: none;
        padding: 0 32px 0 0;
    }

    &--toolbar {
        color: inherit;
        background-color: inherit;
        border: none;
    }

    &:hover {
        cursor: pointer;
    }

    &__text {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        font-size: 14px;

        #{ $this }--minimalistic & {
            font-weight: 700;
        }

        #{ $this }--toolbar & {
            font-size: 13px;
        }
    }

    &__arrow {
        position: absolute;
        right: 0;
        margin-right: 1rem;
    }

    &__list {
        position: absolute;
        border: 1px solid var(--form-inputBorderColor);
        top: 100%;
        left: 0;
        right: 0;
        overflow-y: auto;
        max-height: 20rem;
        width: 100%;
        border-top: none;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        z-index: 10;
        text-align: left;
        font-weight: 400;

        #{ $this }--minimalistic & {
            margin: 0.5rem 0 0 -0.25rem;
        }
    }

    &__item {
        color: var(--form-inputColor);
        background-color: var(--form-inputBackgroundColor);
        position: relative;
        font-size: 14px;

        &:hover {
            cursor: pointer;
        }

        #{ $this }--minimalistic & {
            font-weight: 400;
        }
    }

    &__item-text {
        position: relative;
    }
}
</style>
